<template>
    <Teleport
        v-if="isMounted && props.active"
        to="body"
    >
        <div :class="[$style.holder, props.isGameViewHidden && $style.with_hidden_frame]">
            <div :class="$style.scroller">
                <div :class="$style.scroller_inner">
                    <div :class="$style.content">
                        <slot />
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="props.isTabsOpened"
            :class="$style.scroller_area"
            @click="update(false)"
        />
    </Teleport>
    <slot v-else />
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useBodyScrollLock } from '@/composables/use-body-scroll-lock'

const props = defineProps<{
    active: boolean
    isGameViewHidden: boolean
    isTabsOpened: boolean
}>()

const emit = defineEmits<{
    'update:isTabsOpened': [value: boolean]
}>()

const isMounted = useMounted()

const requestAnimationFrameId = ref<number>()
const scrollLockTimeoutId = ref<number>()

const { setScrollLock } = useBodyScrollLock()

function update(value: boolean) {
    emit('update:isTabsOpened', value)
}

function measure() {
    if (typeof requestAnimationFrameId.value === 'number') {
        cancelAnimationFrame(requestAnimationFrameId.value)
    }

    if (props.isGameViewHidden) {
        setScrollLock(false)
        return
    }

    requestAnimationFrameId.value = requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--isl-vh', `${window.innerHeight * 0.01}px`)

        const screenShort = Math.min(window.screen.width, window.screen.height)
        const viewport = window.visualViewport

        const MOBILE_CHROME_TOP_BAR_HEIGHT = 20 // In Chrome on iOS the top browser bar never fully hides
        const open = !!viewport && viewport.height + viewport.offsetTop < screenShort - MOBILE_CHROME_TOP_BAR_HEIGHT

        if (open === props.isTabsOpened) {
            return
        }

        update(open)

        scrollLockTimeoutId.value = window.setTimeout(
            () => {
                setScrollLock(!open)
            },
            open ? 0 : 100,
        )
    })
}

watch(
    () => props.isGameViewHidden,
    (hidden) => {
        if (!hidden && props.active) {
            measure()
        }
    },
)

onMounted(() => {
    if (!props.active) {
        return
    }
    measure()

    window.addEventListener('resize', measure, { passive: true })
    window.addEventListener('orientationchange', measure, { passive: true })
    window.addEventListener('scroll', measure, { passive: true })
    window.visualViewport?.addEventListener('resize', measure, { passive: true })
    window.visualViewport?.addEventListener('scroll', measure, { passive: true })
})

onBeforeUnmount(() => {
    if (!props.active) {
        return
    }

    window.removeEventListener('resize', measure)
    window.removeEventListener('orientationchange', measure)
    window.removeEventListener('scroll', measure)
    window.visualViewport?.removeEventListener('resize', measure)
    window.visualViewport?.removeEventListener('scroll', measure)

    if (requestAnimationFrameId.value) {
        cancelAnimationFrame(requestAnimationFrameId.value)
    }

    if (scrollLockTimeoutId.value) {
        clearTimeout(scrollLockTimeoutId.value)
    }
})
</script>

<style module>
:root {
    --isl-vh: 1%;
    --mobile-top-bar-height: 40px;
}

.holder {
    position: fixed;
    inset: 0;
    width: 100%;
    height: min(calc(var(--isl-vh) * 100), 100%);
    overflow: hidden;
    z-index: 1;
    display: block;
}

.with_hidden_frame.holder {
    pointer-events: none;
    position: static;
}

.scroller {
    display: block;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.scroller_inner {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    flex-direction: column;
    height: calc(100% + 1px);
    min-height: calc(100% + 1px);
}

.content {
    position: sticky;
    box-sizing: border-box;
    width: 100%;
    height: calc(100% - 1px);
    inset: 0;
    padding: var(--pg-safe-area-top) var(--pg-safe-area-right) 0 var(--pg-safe-area-left);
}

.scroller_area {
    position: fixed;
    height: 400vh;
    inset: var(--mobile-top-bar-height) 0 0 0;
    z-index: 1;
}
</style>
